import {
  CalendarIcon,
  ImageIcon,
  Sparkles,
  VideoIcon,
  Wand2,
  X,
  Maximize,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import EventGenerator from "../components/create/EventGenerator";
import ImageEditor from "../components/create/ImageEditor";
import ImageGenerator from "../components/create/ImageGenerator";
import VideoGenerator from "../components/create/VideoGenerator";
import { Effect } from "../types/effects";
import eventBus from "../utils/eventBus";
import EffectCreationPanel from "../components/create/EffectCreationPanel";

export type GeneratorType =
  | "image"
  | "video"
  | "event"
  | "image-editing"
  | "effects";

const CreatePage: React.FC = () => {
  const location = useLocation();
  const state = location.state as {
    effect?: Effect;
    prompt?: string;
    activeGenerator?: GeneratorType;
    activeSection?: string;
  };
  const effect = state?.effect;
  const prompt = state?.prompt;
  const activeGenerator =
    state?.activeSection === "effects" ? "effects" : state?.activeGenerator;

  const [generatorType, setGeneratorType] = useState<GeneratorType>(
    activeGenerator ? activeGenerator : effect ? "video" : "image"
  );
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (generatorType === "effects") {
      setFullscreen(true);
    }
  }, [generatorType]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && fullscreen) {
        setFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [fullscreen]);

  useEffect(() => {
    if (fullscreen) {
      eventBus.emit("fullscreen:enter");
    } else {
      eventBus.emit("fullscreen:exit");
    }
  }, [fullscreen]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    return () => {
      eventBus.emit("fab:reset");
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10">
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent">
                AI Studio
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center bg-white/5 backdrop-blur-sm border border-white/10 p-1 rounded-lg">
              <button
                onClick={() => setGeneratorType("image")}
                className={`p-1.5 rounded-md text-white transition-all ${
                  generatorType === "image"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg"
                    : "hover:bg-white/10 text-white/60"
                }`}
              >
                <ImageIcon size={14} />
              </button>
              <button
                onClick={() => setGeneratorType("image-editing")}
                className={`p-1.5 rounded-md text-white transition-all ${
                  generatorType === "image-editing"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg"
                    : "hover:bg-white/10 text-white/60"
                }`}
              >
                <Wand2 size={14} />
              </button>
              <button
                onClick={() => setGeneratorType("video")}
                className={`p-1.5 rounded-md text-white transition-all ${
                  generatorType === "video"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg"
                    : "hover:bg-white/10 text-white/60"
                }`}
              >
                <VideoIcon size={14} />
              </button>
              <button
                onClick={() => setGeneratorType("event")}
                className={`p-1.5 rounded-md text-white transition-all ${
                  generatorType === "event"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg"
                    : "hover:bg-white/10 text-white/60"
                }`}
              >
                <CalendarIcon size={14} />
              </button>
              <button
                onClick={() => setGeneratorType("effects")}
                className={`p-1.5 rounded-md text-white transition-all ${
                  generatorType === "effects"
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg"
                    : "hover:bg-white/10 text-white/60"
                }`}
              >
                <Sparkles size={14} />
              </button>
            </div>
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="p-2 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300"
            >
              {fullscreen ? (
                <X size={16} className="text-white/80" />
              ) : (
                <Maximize size={16} className="text-white/80" />
              )}
            </button>
          </div>
        </header>

        <div key={generatorType}>
          {generatorType === "image" && <ImageGenerator prompt={prompt} />}
          {generatorType === "video" && <VideoGenerator effect={effect} />}
          {generatorType === "event" && <EventGenerator />}
          {generatorType === "image-editing" && <ImageEditor />}
          {generatorType === "effects" && <EffectCreationPanel />}
        </div>
        {/* <AnimatePresence mode="sync">
        </AnimatePresence> */}
      </div>
    </div>
  );
};

export default CreatePage;
